/**
 * Notification category + channel typings for the parent-facing preferences
 * card on /settings/account.
 *
 * The catalog is NOT defined here any more. Categories, labels, descriptions,
 * supported channels, and the shipped default matrix all come from
 * `@ordo/constants/notification-rules` — the same registry the server resolves
 * against and the Customers admin console renders. Three hand-kept copies of
 * that matrix used to exist and only the category NAMES were pinned, so a
 * default could flip server-side while this card kept rendering the old state.
 * That is the PROD-383 drift class; the shared registry is the fix.
 *
 * What stays app-local is `categoryAppliesToUser` below: which rows a given
 * persona should even see is an ordo nav concern, not registry data.
 */

import {
  NOTIFICATION_RULE_SECTION_LABELS,
  NOTIFICATION_RULES,
  type NotificationChannelMatrix,
  type NotificationRuleCategoryKey,
  type NotificationRuleChannel,
  type NotificationRuleSection,
} from '@ordo/constants/notification-rules'

export type NotificationCategory = NotificationRuleCategoryKey
export type NotificationChannel = NotificationRuleChannel

/** Rules that ride the notification rail, i.e. the ones with a category. */
const CATEGORY_RULES = NOTIFICATION_RULES.filter((rule) => rule.category !== undefined)

/**
 * Per-(category, channel) default opt-in, used to compute a toggle's displayed
 * state when the user has saved no explicit `NotificationPreference` row.
 */
export const NOTIFICATION_DEFAULTS = Object.fromEntries(
  CATEGORY_RULES.map((rule) => [rule.category, rule.defaults]),
) as Record<NotificationCategory, NotificationChannelMatrix>

export interface NotificationCategorySpec {
  category: NotificationCategory
  section: NotificationRuleSection
  label: string
  description: string
  /** Channels this category supports — others are hidden in the row. */
  channels: NotificationChannel[]
}

/** Every category the preferences UI can render, in registry order. */
export const NOTIFICATION_CATEGORIES: readonly NotificationCategorySpec[] = CATEGORY_RULES.map((rule) => ({
  category: rule.category as NotificationCategory,
  section: rule.section,
  label: rule.label,
  description: rule.description,
  channels: [...rule.channels],
}))

export const NOTIFICATION_SECTION_LABELS = NOTIFICATION_RULE_SECTION_LABELS

/**
 * Per-category visibility rule against the user's effective
 * permissions / profile.
 *
 * The plan calls out the "task assigned" row specifically — it should
 * only render when the user can see the Tasks page. We approximate
 * `tasks.view` from the user's resolved nav (server-provided in
 * `useProfile().profile.nav.items`) — if a category does not have a
 * known gating predicate, it is treated as universally applicable.
 *
 * Pass `tasksVisible` (true when the user has Tasks in their nav) +
 * any other context we wire up later (family/billing/etc. are
 * universal today).
 */
export function categoryAppliesToUser(
  spec: NotificationCategorySpec,
  ctx: {
    /** Tasks nav item is visible to this user (proxy for `tasks.view`). */
    tasksVisible: boolean
    /** Production-records nav item is visible (proxy for chef/school staff). */
    productionVisible: boolean
    /** Cafeteria nav item is visible (proxy for school staff). */
    cafeteriaVisible: boolean
    /** My Family nav item is visible (proxy for PARENT). */
    familyVisible: boolean
    /** Billing nav item is visible (universal but kept as a gate). */
    billingVisible: boolean
  },
): boolean {
  switch (spec.category) {
    case 'TASK_ASSIGNED':
      return ctx.tasksVisible
    case 'CHILD_SERVED_MEAL':
    case 'CHILD_UNSERVED_FLAGGED':
      return ctx.familyVisible
    case 'PRODUCTION_RECORD_DUE':
      return ctx.productionVisible
    case 'SHIPMENT_STATUS_CHANGE':
      return ctx.productionVisible || ctx.cafeteriaVisible
    case 'DEAL_STAGE_CHANGE':
    case 'RFP_BID_DUE':
      // Internal-only — surfaced when the user has tasks (a proxy for
      // ORDO_ADMIN / CHEF_ADMIN; the precise gate ships with Plan 27).
      return ctx.tasksVisible
    case 'BALANCE_CLAWBACK':
      return false
    case 'PAYMENT_FAILED':
    case 'PAYMENT_RECEIVED':
    case 'ACCOUNT_LOW_BALANCE':
    case 'ACCOUNT_INVITATION':
      return ctx.billingVisible
    default:
      return true
  }
}
